import * as React from 'react';
import { styles } from './styles';
import { scrollToYCasually } from 'utils/scrollToY';
import {
  DesignShowcase_designShowcase_exhibits,
  DesignShowcase_designShowcase,
  DesignShowcase_designShowcase_organization_themes_buckets,
  DesignShowcase_designShowcase_organization_colors_buckets
} from '@graphql/designShowcaseTypes';

import { DesignSection } from '../DesignDemo/types';
import { buildReactStateHandler, isEqual, makeDebugger } from '@lib';
import { Spinner } from '@components/Spinner';
import { api } from './modules/api';
import memoizeOne from 'memoize-one';
import { ShowcaseHeader } from './components/ShowcaseHeader';
import { HeadSection } from './components/HeadSection';
import { DesignExhibitList } from './components/DesignExhibitList';
import { InlineDialogProps } from './components/InlineDialog';
import { ShowcaseFilters } from './ShowcaseFilters';
import { NoMatchesMessage } from './components/NoMatchesMessage';
import { telemetry } from '@services/tracking/telemetry';
import uuid from 'uuid/v4';
import { history } from 'browserHistory';
import { generateCreateEventQueryString } from './util/generateCreateEventQueryString';
import MediaQuery from 'react-responsive';
import { MarketingNav } from '../components/MarketingNav';
import { DEFAULT_EXHIBIT_DATA } from './defaultExhibitData';
import { isAndroidOrIOS } from 'utils/isAndroidOrIOS';
import { RouteComponentProps } from 'react-router';
import { getMarketingEnv } from 'utils/getJoyEnv';

const debug = makeDebugger('DesignShowcase');
const stateUpdater = buildReactStateHandler<DesignShowcaseState>();
export type LayoutOption = 'aloha' | 'brannan';
interface Props {
  history: RouteComponentProps['history'];
}

const layoutOptions: { label: string; value: string }[] = [
  { label: 'Aloha', value: 'aloha' },
  { label: 'Brannan', value: 'brannan' }
];

export const SHOWCASE_CONSTANTS = {
  THEME_MOST_POPULAR: 'most_popular'
};

export interface DesignShowcaseSectionConfig<T> {
  open: boolean;
  values: T[];
}

export interface ThemeDesignShowcaseSectionConfig
  extends DesignShowcaseSectionConfig<DesignShowcase_designShowcase_organization_themes_buckets> {
  showMostPopular: boolean;
}

export type DesignShowcaseState = Readonly<{
  hasFinishedRequestingData: boolean;
  hasFailedToLoad: boolean;
  layout: DesignShowcaseSectionConfig<string>;
  themes: ThemeDesignShowcaseSectionConfig;
  colors: DesignShowcaseSectionConfig<DesignShowcase_designShowcase_organization_colors_buckets>;
  invites: DesignShowcaseSectionConfig<string>;
  showMobileNav: boolean;
  mobileNavOpen: boolean;
}>;

type OrganizationBucketGroup = 'colors' | 'themes';
type OrganizationBucket =
  | DesignShowcase_designShowcase_organization_themes_buckets
  | DesignShowcase_designShowcase_organization_colors_buckets;

type OnBucketSelectOptions = {
  multiple?: boolean;
  closeOnSelect?: boolean;
};

let hasRefiltered = false;

export class DesignShowcase extends React.PureComponent<Props, DesignShowcaseState> {
  private navRef: React.RefObject<HTMLDivElement> = React.createRef();

  private notMobile = !isAndroidOrIOS();
  readonly state = {
    hasFinishedRequestingData: false,
    hasFailedToLoad: false,
    layout: {
      open: false,
      values: []
    },
    themes: {
      open: false,
      showMostPopular: true,
      values: [] as DesignShowcaseState['themes']['values']
    },
    colors: {
      open: false,
      values: [] as DesignShowcaseState['colors']['values']
    },
    invites: {
      open: false,
      values: []
    },
    showMobileNav: false,
    mobileNavOpen: false
  };

  showcaseData: DesignShowcase_designShowcase;
  triggerRef: HTMLElement | null;
  exhibitListId = uuid();

  componentDidMount() {
    this.requestShowcase();
    telemetry.sendEvent({
      type: 'page',
      name: 'marketing.design'
    });
  }

  requestShowcase = async () => {
    try {
      const response = await api.requestData();
      if (response && response.designShowcase) {
        this.showcaseData = response.designShowcase;
        this.setState(
          stateUpdater(draft => {
            draft.hasFinishedRequestingData = true;
          })
        );
      }
    } catch (e) {
      debug(e);
      this.showcaseData = {
        __typename: 'DesignShowcase',
        organization: {} as any,
        exhibits: DEFAULT_EXHIBIT_DATA as DesignShowcase_designShowcase_exhibits[]
      };
      this.setState(
        stateUpdater(draft => {
          draft.hasFinishedRequestingData = true;
          draft.hasFailedToLoad = true;
        })
      );
    }
  };

  // ----------------------------------------
  // Callback handling
  // ----------------------------------------

  handleTriggerClick: React.MouseEventHandler<HTMLDivElement> = e => {
    const section = e.currentTarget.dataset.section as DesignSection;
    if (!section) {
      debug('Trigger must have data-section attribute');
      return;
    }
    this.setState(
      stateUpdater(draft => {
        ['layout', 'colors', 'themes', 'invites']
          .filter(x => x !== section)
          .forEach(sectionToClose => {
            if (draft[sectionToClose]) {
              draft[sectionToClose].open = false;
            }
          });
        if (draft[section] !== undefined) {
          draft[section].open = !draft[section].open;
        }
      })
    );
  };

  extraInfo = (
    newValue?: any,
    section?: keyof Omit<
      DesignShowcaseState,
      'hasFinishedRequestingData' | '__showcaseData' | 'displayedExhibits' | 'showMobileNav'
    >
  ) => ({
    activeFilterInvites:
      [...this.state.invites.values.map(c => c), section === 'invites' && newValue]
        .filter(val => !!val)
        .sort()
        .join(', ') || 'all',
    activeFilterThemeType:
      [...this.state.themes.values.map(c => c.bucketId), section === 'themes' && newValue]
        .filter(val => !!val)
        .sort()
        .join(', ') || 'all',
    activeFilterColor:
      [...this.state.colors.values.map(c => c.bucketId), section === 'colors' && newValue]
        .filter(val => !!val)
        .sort()
        .join(', ') || 'all',
    activeFilterLayout:
      [...this.state.layout.values, section === 'layout' && newValue]
        .filter(val => !!val)
        .sort()
        .join(', ') || 'all'
  });

  handleOptionSelect: React.MouseEventHandler<HTMLButtonElement> = e => {
    const section = e.currentTarget.dataset.section as keyof Omit<
      DesignShowcaseState,
      'hasFinishedRequestingData' | '__showcaseData' | 'displayedExhibits' | 'showMobileNav'
    >;
    if (!section) {
      debug('Option must have data-section attribute');
      return;
    }
    const value = e.currentTarget.value;
    if (!value) {
      debug('Option must have a value');
      return;
    }
    switch (section) {
      case 'themes':
        if (value === SHOWCASE_CONSTANTS.THEME_MOST_POPULAR) {
          hasRefiltered = true;
          this.setState(
            stateUpdater(draft => {
              draft.themes.values = [];
              draft.themes.open = false;
              draft.themes.showMostPopular = true;
            })
          );
        } else {
          this.handleBucketSelect<DesignShowcase_designShowcase_organization_themes_buckets>('themes', value, {
            closeOnSelect: true
          });
        }
        telemetry.sendEvent({
          category: 'marketing.designs',
          action: 'FilterByTheme',
          label: value,
          extraInfo: this.extraInfo(value, section)
        });
        break;
      case 'colors':
        this.handleBucketSelect<DesignShowcase_designShowcase_organization_colors_buckets>('colors', value, {
          closeOnSelect: false,
          multiple: true
        });
        const colorsSet = new Set(this.state.colors.values.map(val => val.bucketId));
        if (!colorsSet.has(value)) {
          telemetry.sendEvent({
            category: 'marketing.designs',
            action: 'FilterByColor',
            label: value,
            extraInfo: this.extraInfo(value, section)
          });
        }
        break;
      case 'invites':
        hasRefiltered = true;
        this.setState(
          stateUpdater(draft => {
            const designSection = draft[section];
            if (designSection) {
              if (designSection.values.includes(value)) {
                designSection.values = designSection.values.filter(v => v !== value);
              } else {
                designSection.values.push(value);
              }
            }
          })
        );
        const inviteSet: Array<string> = this.state.layout.values;
        if (!inviteSet.includes(value)) {
          telemetry.sendEvent({
            category: 'marketing.designs',
            action: 'FilterByInvites',
            label: value,
            extraInfo: this.extraInfo(value, section)
          });
        }
        break;
      case 'layout':
        hasRefiltered = true;
        this.setState(
          stateUpdater(draft => {
            const designSection = draft[section];
            if (designSection) {
              designSection.values = [value];
              // if (designSection.values.includes(value)) {
              //   designSection.values = designSection.values.filter(v => v !== value);
              // } else {
              //   designSection.values.push(value);
              // }
              draft.themes.open = false;
              designSection.open = false;
            }
          })
        );
        const layoutSet: Array<string> = this.state.layout.values;
        if (!layoutSet.includes(value)) {
          telemetry.sendEvent({
            category: 'marketing.designs',
            action: 'FilterByLayout',
            label: value,
            extraInfo: this.extraInfo(value, section)
          });
        }
        break;
      default:
    }
    scrollToYCasually(0);
  };

  handleOnCloseRequest: InlineDialogProps<null>['onCloseRequest'] = (e, contextData) => {
    this.setState(
      stateUpdater(draft => {
        draft.themes.open = false;
        draft.layout.open = false;
        draft.colors.open = false;
      })
    );
  };

  handleOnResetClick: React.MouseEventHandler<HTMLElement> = e => {
    const section = e.currentTarget.dataset.section as keyof Omit<
      DesignShowcaseState,
      | 'hasFinishedRequestingData'
      | '__showcaseData'
      | 'displayedExhibits'
      | 'showMobileNav'
      | 'hasFailedToLoad'
      | 'mobileNavOpen'
    >;
    if (!section) {
      debug('Show all button must have data-section attribute');
      return;
    }

    hasRefiltered = true;

    this.setState(
      stateUpdater(draft => {
        draft[section].values = [];
      })
    );
    scrollToYCasually(0);
  };

  handleStartHereClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    if (e.currentTarget) {
      const targetExhibitId = e.currentTarget.value;
      const targetExhibit = this.showcaseData.exhibits.find(exhibit => exhibit.exhibitId === targetExhibitId);
      if (targetExhibit) {
        let queryString = generateCreateEventQueryString(targetExhibit);
        const createUrl = queryString ? `/createwedding?${queryString}` : '/createwedding';
        debug(`Navigating to create event: ${createUrl}`);
        this.handleRedirectTelemtry(targetExhibit, 'StartCreate', createUrl);
        const url = `${getMarketingEnv(window.joyEnv)}${createUrl}`;
        window.location.href = url;
      } else {
        debug(`Unable to find exhibit to start from: exhibitId: ${targetExhibitId}`);
      }
    }
  };

  handleFilterButtonClick: React.MouseEventHandler<HTMLMainElement> = () => {
    this.setState({ showMobileNav: true });
  };

  handleMobileNavOpen: React.MouseEventHandler<HTMLMainElement> = () => {
    this.setState({ mobileNavOpen: true });
  };

  handleMobileNavClose: React.MouseEventHandler<HTMLMainElement> = () => {
    this.setState({ mobileNavOpen: false });
  };

  handleOnPreviewThisClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    const targetExhibitId = e.currentTarget.value;
    const targetExhibit = this.showcaseData.exhibits.find(exhibit => exhibit.exhibitId === targetExhibitId);

    if (targetExhibit) {
      let queryString = generateCreateEventQueryString(targetExhibit);
      const redirectUrl = `/preview${queryString ? `?${queryString}` : ''}`;
      this.handleRedirectTelemtry(targetExhibit, 'PreviewDesignClicked', redirectUrl);
      debug(`Navigating to demo design`);

      history.push(redirectUrl);
    } else {
      debug(`Unable to find exhibit to preview: exhibitId: ${targetExhibitId}`);
    }
  };

  handleDone: React.MouseEventHandler<HTMLMainElement> = e => {
    this.setState(
      stateUpdater(draft => {
        draft.showMobileNav = false;
      })
    );
  };

  handleMainClick: React.MouseEventHandler<HTMLMainElement> = e => {
    if (
      this.state.showMobileNav &&
      this.navRef.current &&
      e.target instanceof HTMLElement &&
      !this.navRef.current.contains(e.target)
    ) {
      this.setState(
        stateUpdater(draft => {
          draft.showMobileNav = false;
        })
      );
    }
  };

  handleResetFilters: React.MouseEventHandler<HTMLButtonElement> = e => {
    this.setState(
      stateUpdater(draft => {
        draft.colors.values = [];
        draft.layout.values = [];
        draft.themes.values = [];
        draft.invites.values = [];
      })
    );
  };

  // ----------------------------------------
  // Render
  // ----------------------------------------

  render(): React.ReactNode {
    const { themes, layout, colors, hasFinishedRequestingData, invites, hasFailedToLoad } = this.state;

    if (hasRefiltered) {
      hasRefiltered = false;
      const nextExhibitListId = uuid();
      debug(
        `A filter has been applied. Reassigning exhibitListId from '${this.exhibitListId}' to '${nextExhibitListId}' `
      );
      this.exhibitListId = nextExhibitListId;
    }

    if (!hasFinishedRequestingData) {
      return <Spinner />;
    }

    let displayedExhibits = this.getFilteredExhibits(themes.values, layout.values, colors.values, invites.values);
    const noMatchesFound = displayedExhibits.length === 0;
    if (noMatchesFound) {
      // When no matches are found, show a list of relevant theme exhibits
      debug(`No matches found - defaulting to relevant theme - ${themes.values[0]} - exhibits`);
      telemetry.sendEvent({
        category: 'marketing.designs',
        action: 'GetFilteredExhibits',
        label: 'noMatchesFound',
        extraInfo: this.extraInfo()
      });
      displayedExhibits = this.getFilteredExhibits(themes.values, [], [], []);
    }

    debug('DISPLAYED EXHIBITS: ', displayedExhibits);

    return (
      <>
        <HeadSection />

        <MediaQuery minWidth={1000}>
          {showMarketingSiteNav => (
            <>
              <div id="mobile-marketing-nav">
                {showMarketingSiteNav ? (
                  <MarketingNav
                    navType="desktop"
                    mobileNavOpen={false}
                    handleMobileNavClose={() => 'noop'}
                    handleMobileNavOpen={() => 'noop'}
                  />
                ) : (
                  <MarketingNav
                    navType="mobile"
                    mobileNavOpen={this.state.mobileNavOpen}
                    handleMobileNavClose={this.handleMobileNavClose}
                    handleMobileNavOpen={this.handleMobileNavOpen}
                  />
                )}
              </div>

              <main className={styles.container} onClick={this.handleMainClick}>
                <ShowcaseHeader notMobile={this.notMobile} />
                {!hasFailedToLoad && (
                  <ShowcaseFilters
                    handleFilterButtonClick={this.handleFilterButtonClick}
                    themeState={themes}
                    invitesState={invites}
                    themeBuckets={this.showcaseData.organization.themes.buckets}
                    layoutState={layout}
                    layoutOptions={layoutOptions}
                    showMobileNav={this.state.showMobileNav}
                    forwardRef={this.navRef}
                    colorState={colors}
                    colorBuckets={this.showcaseData.organization.colors.buckets}
                    handleTriggerClick={this.handleTriggerClick}
                    handleOptionSelect={this.handleOptionSelect}
                    handleOnCloseRequest={this.handleOnCloseRequest}
                    handleOnResetClick={this.handleOnResetClick}
                    handleDone={this.handleDone}
                  />
                )}
                {noMatchesFound && <NoMatchesMessage onResetFiltersClick={this.handleResetFilters} />}
                <DesignExhibitList
                  key={this.exhibitListId}
                  onStartHereClick={this.handleStartHereClick}
                  onPreviewThisClick={this.handleOnPreviewThisClick}
                  exhibits={displayedExhibits}
                  showDesktopButtons={this.notMobile}
                />
                {hasFailedToLoad && (
                  <div className={styles.getStartedContainer}>
                    <span>This is just a glimpse</span>
                    <p>
                      Start customizing now to try out hundreds of other possible combinations to create your perfect
                      look.
                    </p>
                    <a className={styles.getStarted()} href="https://withjoy.com/createWedding">
                      Get Started
                    </a>
                  </div>
                )}
              </main>
            </>
          )}
        </MediaQuery>
      </>
    );
  }

  // ----------------------------------------
  // Helpers
  // ----------------------------------------

  private handleRedirectTelemtry = (
    targetExhibit: DesignShowcase_designShowcase_exhibits,
    action: string,
    redirectUrl: string
  ) => {
    let styleId = '';
    try {
      styleId = targetExhibit!.spec!.style!.id || '';
    } finally {
      //
    }
    const primaryPhotoHorizontal = (targetExhibit.spec || ({ primaryPhotoHorizontal: { assetId: '' } } as any))
      .primaryPhotoHorizontal.assetId;
    const fontName = (targetExhibit.spec || ({ fontName: '' } as any)).fontName;
    const baseColor = (targetExhibit.spec || ({ palette: { baseColor: { hexString: '' } } } as any)).palette.baseColor
      .hexString;
    const accentColor = (targetExhibit.spec || ({ palette: { accentColor: { hexString: '' } } } as any)).palette
      .accentColor.hexString;
    const usesWeddingColor = (targetExhibit.spec || ({ usesWeddingColor: '' } as any)).usesWeddingColor as
      | boolean
      | string;
    telemetry.sendEvent({
      category: 'marketing.designs',
      action,
      label: styleId,
      extraInfo: {
        ...this.extraInfo(),
        url: redirectUrl,
        template: targetExhibit!.spec!.websiteLayout!.template || '',
        exhibitId: targetExhibit.exhibitId,
        style: styleId,
        primaryPhotoHorizontal: primaryPhotoHorizontal,
        fontName,
        baseColor,
        accentColor,
        usesWeddingColor
      }
    });
  };

  getFilteredExhibits = memoizeOne(
    (
      themes: DesignShowcaseState['themes']['values'],
      layouts: DesignShowcaseState['layout']['values'],
      colors: DesignShowcaseState['colors']['values'],
      invites: DesignShowcaseState['invites']['values']
    ): DesignShowcase_designShowcase_exhibits[] => {
      debug('getFilteredExhibits()');
      let exhibits = this.showcaseData.exhibits;
      const acceptAllLayouts = layouts.length === 0 || layouts.length === layoutOptions.length;
      const acceptAllThemes = themes.length === 0;
      const acceptAllColors =
        colors.length === 0 || colors.length === this.showcaseData.organization.colors.buckets.length;

      const matchingPrintInvites = invites.find(el => el === 'print');
      const matchingEcardInvites = invites.find(el => el === 'ecard');

      if (acceptAllLayouts && acceptAllThemes && acceptAllColors && !matchingPrintInvites && !matchingEcardInvites) {
        return exhibits;
      }

      if (!acceptAllLayouts) {
        exhibits = exhibits.filter(exhibit => {
          if (exhibit.spec && exhibit.spec.websiteLayout) {
            return layouts.includes(exhibit.spec.websiteLayout.template);
          }
          return false;
        });
      }

      if (!acceptAllColors || !acceptAllThemes) {
        const themeBucketIds = new Set(themes.map(theme => theme.bucketId));
        const colorBucketIds = new Set(colors.map(color => color.bucketId));

        exhibits = exhibits.filter(exhibit => {
          const test = this.filterExhibitBuckets(
            exhibit,
            acceptAllThemes,
            acceptAllColors,
            themeBucketIds,
            colorBucketIds
          );
          return test;
        });
      }

      if (matchingPrintInvites) {
        exhibits = exhibits.filter(
          exhibit => !!exhibit.spec && !!exhibit.spec.style && exhibit.spec.style.supportsPrints
        );
      }

      if (matchingEcardInvites) {
        exhibits = exhibits.filter(
          exhibit => !!exhibit.spec && !!exhibit.spec.style && exhibit.spec.style.supportsEcards
        );
      }

      return exhibits; // exhibits;
    },
    isEqual
  );

  filterExhibitBuckets = (
    exhibit: DesignShowcase_designShowcase_exhibits,
    acceptAllThemes: boolean,
    acceptAllColors: boolean,
    themeBucketIds: Set<string>,
    colorBucketIds: Set<string>
  ) => {
    let colorMatch = acceptAllColors;
    let themeMatch = acceptAllThemes; // All themes
    for (let index = 0; index < exhibit.organizationBuckets.length; index++) {
      const orgBucket = exhibit.organizationBuckets[index];
      if (!acceptAllColors && orgBucket.bucketGroupId === 'colors' && !colorMatch) {
        colorMatch = colorBucketIds.has(orgBucket.bucketId);
      }
      if (!acceptAllThemes && orgBucket.bucketGroupId === 'themes' && !themeMatch) {
        themeMatch = themeBucketIds.has(orgBucket.bucketId);
      }
    }
    const result = themeMatch && colorMatch;
    return result;
  };

  private handleBucketSelect = <T extends OrganizationBucket>(
    bucketGroupId: OrganizationBucketGroup,
    bucketId: string,
    options: OnBucketSelectOptions = {}
  ) => {
    const showcaseData = this.showcaseData;
    if (showcaseData) {
      const organizationBucketGroup = showcaseData.organization[bucketGroupId].buckets;
      const organizationBucket = organizationBucketGroup.find(option => !!option && option.bucketId === bucketId);

      if (organizationBucket) {
        hasRefiltered = true;
        this.setState(
          stateUpdater(draft => {
            const bucketDraft = draft[bucketGroupId] as DesignShowcaseSectionConfig<T>;
            if (bucketDraft.values.some(group => group.bucketId === organizationBucket.bucketId)) {
              bucketDraft.values = bucketDraft.values.filter(v => v.bucketId !== organizationBucket.bucketId);
            } else {
              if (options.multiple) {
                // There can be multiple selected optins
                bucketDraft.values.push(organizationBucket as T);
              } else {
                bucketDraft.values = [organizationBucket as T];
              }
            }

            if (bucketGroupId === 'themes') {
              draft.themes.showMostPopular = false;
            }

            if (options.closeOnSelect) {
              bucketDraft.open = false;
            }
          })
        );
      } else {
        debug(`Bucket option not found: bucketGroupId: ${bucketGroupId} bucketId: ${bucketId}`);
      }
    }
  };
}
